import React, { useState, useEffect } from 'react';
import { CircleCheck, Square, SquareCheck, ChevronDown } from 'lucide-react';
import { type SkillPathway, dashboardService } from '@/services/dashboardService';
import './PathwaySelector.css';

interface PathwaySelectorProps {
  pathways: SkillPathway[];
  groupedPathways?: { [category: string]: SkillPathway[] };
  selectedPathway: string | null;
  onPathwaySelect?: (pathwayId: string) => void;
  onPathwayEnroll?: (pathwayId: string) => void;
  onTreeSelect?: (tree: SkillPathway) => void;
  loading?: boolean;
}

export const PathwaySelector: React.FC<PathwaySelectorProps> = ({ 
  pathways, 
  groupedPathways,
  selectedPathway,
  onPathwaySelect,
  onPathwayEnroll,
  onTreeSelect,
  loading = false
}) => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [expandedPathways, setExpandedPathways] = useState<Set<string>>(new Set());
  const [isAnimating, setIsAnimating] = useState<Set<string>>(new Set());
  const [selectedTree, setSelectedTree] = useState<SkillPathway|null>(null);

  useEffect(() => {
    if (!loading && pathways && pathways?.length > 0 && !selectedTree) {
      
      if (groupedPathways) {
        const firstCategoryKey = Object.keys(groupedPathways)[0];
        const firstCategoryTrees = groupedPathways[firstCategoryKey];
        if (firstCategoryTrees && firstCategoryTrees.length > 0) {
          const firstTree = firstCategoryTrees[0];
          setSelectedTree(firstTree);
          onTreeSelect?.(firstTree);
          
          if (firstCategoryKey !== 'All Pathways') {
            setExpandedCategories(prev => new Set(prev).add(firstCategoryKey));
          }
        }
      } else if (pathways.length > 0) {
        const firstTree = pathways[0];
        setSelectedTree(firstTree);
        onTreeSelect?.(firstTree);
      }
    }
  }, [loading, pathways, groupedPathways, selectedTree, onTreeSelect]);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const togglePathway = (pathwayId: string) => {
    setExpandedPathways(prev => {
      const newSet = new Set(prev);
      // If the pathway is already expanded, close it
      if (newSet.has(pathwayId)) {
        newSet.delete(pathwayId);
      } else {
        // Otherwise, clear all other expanded pathways and open this one
        newSet.clear();
        newSet.add(pathwayId);
      }
      return newSet;
    });
  };

  const handlePathwaySelect = async (pathwayId: string) => {
    try {
      // await dashboardService.selectPathway(pathwayId);
      onPathwaySelect?.(pathwayId);
    } catch (error) {
      console.error('Failed to select pathway:', error);
    }
  };

  const handlePathwayEnroll = async (pathwayId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await dashboardService.enrollInPathway(pathwayId);
      onPathwayEnroll?.(pathwayId);
    } catch (error) {
      console.error('Failed to enroll in pathway:', error);
    }
  };

  const handlePathwayClick = (pathwayId: string, hasSkillTrees: boolean|undefined) => {
    if (hasSkillTrees) {
      // Also close any expanded categories when toggling pathways
      setExpandedCategories(new Set());
      togglePathway(pathwayId);
    } else {
      handlePathwaySelect(pathwayId);
    }
  };

  const handleTreeClick = (tree: any) => {
    // console.log(tree);
    setSelectedTree(tree);
    if (onTreeSelect) onTreeSelect(tree);
  };

  const isCategoryExpanded = (category: string) => expandedCategories.has(category);
  const isPathwayExpanded = (pathwayId: string) => expandedPathways.has(pathwayId);

  const isExpanded = (pathwayId: string) => expandedPathways.has(pathwayId);
  const isAnimatingPathway = (pathwayId: string) => isAnimating.has(pathwayId);

  const checkCompletion = (tree: any) => {
    let completed = false;
  
      const firstVersion = tree.versions && tree.versions.length > 0 
        ? tree.versions[0] 
        : null;
      
      if (firstVersion) {
        // Check completion status - handle both string and number for learning_progress
        const learningProgressNum = typeof firstVersion.learning_progress === 'number' 
          ? firstVersion.learning_progress 
          : parseFloat(firstVersion.learning_progress) || 0;
        
        const isCompleted = firstVersion.learning_status === 'completed' || learningProgressNum >= 100;
        
        // Also check if all microskills are completed as a fallback
        let allMicroskillsCompleted = false;
        if (firstVersion.micro_skills && Array.isArray(firstVersion.micro_skills)) {
          const totalMicroskills = firstVersion.micro_skills.length;
          const completedMicroskills = firstVersion.micro_skills.filter(
            (ms: any) => ms.learning_status === 'completed' || ms.learning_progress >= 100
          ).length;
          allMicroskillsCompleted = totalMicroskills > 0 && completedMicroskills === totalMicroskills;
        }
        
        // Update completion status state
        completed = isCompleted || allMicroskillsCompleted;
      }
      
    return completed;
  }

  // Use grouped pathways if available, otherwise use flat list
  const displayData = groupedPathways ? Object.entries(groupedPathways) : [['All Pathways', pathways]];

  return (
    <div className="bg-transparent">
      <div className="relative text-lg font-light italic mb-4 bg-white/10 rounded-sm p-2">
      <img src="/pages/dashboard/office job.svg" className="absolute inline top-3 left-3 text-start h-5" />
        <h3 className="inline ml-9">
          {loading ? (
            <div className="ml-12 -mt-6 animate-pulse bg-white/70 rounded h-6 w-48"></div>
          ) : (
            selectedTree ? selectedTree.name : 'My Skillstree Pathway'
          )}
        </h3>
      </div>
      
      <div className="min-h-72 max-h-72 p-4 bg-blue-950/90 rounded-sm justify-center items-center shadow-2xl shadow-white/50 overflow-y-auto overflow-x-clip
        /* Custom scrollbar styling */
        scrollbar-thin scrollbar-track-transparent scrollbar-thumb-black/40
        hover:scrollbar-thumb-black/60 scrollbar-thumb-rounded-full
        scrollbar-corner-transparent
        /* Firefox */
        scrollbar-width: thin;
        scrollbar-color: rgba(0, 0, 0, 0.4) transparent;
      ">
        {loading ? (
          // Loading skeleton for pathways
          <div className="space-y-2">
            {[...Array(4)].map((_, index) => (
              <div key={index} className="space-y-1">
                <div className="p-3 flex items-center justify-between rounded-lg bg-black/40 animate-pulse">
                  <div className="w-11/12 flex items-start justify-start">
                    {/* <div className="w-5 h-5 bg-white/70 rounded mt-1 flex-shrink-0"></div> */}
                    <div className="w-10/12 ms-2 h-6 bg-white/70 rounded"></div>
                  </div>
                  <div className="w-6 h-6 bg-white/70 rounded flex-shrink-0"></div>
                </div>
              </div>
            ))}
          </div>
        ) : displayData.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <span className="text-white text-base font-normal italic leading-6 text-center">
              Selected Skillstree Pathway <br/>will be shown here
            </span>
          </div>
        ) : (
          <div className="space-y-2">
            {displayData.map(([pathway, skillstrees]) => {
              const hasSkillTrees = skillstrees && (skillstrees as any).length > 0;
              const expanded = isExpanded(pathway as string);
              const animating = isAnimatingPathway(pathway as string);
              // const isCompleted = !(skillstrees as SkillPathway[])?.some(tree => tree.is_enrolled == false);
              
              return (
                <div key={pathway as string} className="space-y-1">
                  {/* Main Pathway */}
                  <div
                    className={`p-3 flex items-center justify-between rounded-lg cursor-pointer transition-all duration-300 bg-black/40 hover:bg-white/10 ${animating ? 'opacity-70 scale-95' : ''}`}
                    onClick={() => handlePathwayClick(pathway as string, hasSkillTrees as boolean)}
                  >
                    <div className="w-11/12 flex items-start justify-start">
                      {/* {!isCompleted ? (
                        <SquareCheck size={20} className="w-1/12 text-white mt-1 flex-shrink-0"/>
                      ) : (
                        <Square size={20} className="w-1/12 text-white mt-1 flex-shrink-0"/>
                      )} */}
                      <span className="w-11/12 text-start ms-2 text-lg font-semibold line-clamp-1">
                        {pathway as string}
                      </span>
                    </div>
                    
                    {hasSkillTrees && (
                      <ChevronDown 
                        size={24} 
                        strokeWidth={3} 
                        className={`w-1/12 text-white text-end mt-0.5 flex-shrink-0 transition-transform duration-300 ${
                          expanded ? 'rotate-180' : ''
                        }`}
                      />
                    )}
                  </div>
                  
                  {/* Skill Trees - Animated Container */}
                  {hasSkillTrees && (
                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        expanded || isCategoryExpanded(pathway as string)
                          ? 'max-h-96 opacity-100' 
                          : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div className="space-y-1 ml-4">
                        {(skillstrees as any)?.map((tree) => {
                          const isSelected = selectedTree?.id === tree.id;
                          const isCompleted = checkCompletion(tree);
                          
                          return (
                          <div
                            key={tree.id}
                            className={`p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                              isSelected 
                                ? 'bg-orange-600 text-white transform scale-105' 
                                : tree.is_enrolled
                                ? 'bg-transparent'
                                : 'bg-transparent hover:bg-white/10'
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTreeClick(tree);
                            }}
                          >
                            <div className="flex items-start justify-start">
                              {isSelected ? (
                                <CircleCheck size={20} className="w-1/12 text-white mt-0.5 flex-shrink-0"/>
                              ) : isCompleted ? (
                                <CircleCheck 
                                  size={23} 
                                  fill="green" 
                                  strokeWidth={2} 
                                  className="w-1/12 text-blue-950 mt-0.5 flex-shrink-0"
                                />
                              ) : (
                                <CircleCheck size={20} className="w-1/12 text-yellow-500 mt-0.5 flex-shrink-0"/>
                              )}
                              <span className="w-11/12 ms-2 text-start font-medium line-clamp-1">
                                {tree.name}
                              </span>
                            </div>
                          </div>
                        )})}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};